/**
 * Train Simulator editor schema.
 *
 * Declares the grid, required gameplay layers, paintable tile palette
 * and entity types (with their positioning mode + editable props) so
 * the generic StageEditorScene can render an editor for trainsim
 * without any trainsim-specific code.
 *
 * Other games will register their own schemas later. The shape is
 * intentionally future-proofed for a 2D platformer:
 *   - positioning: 'tile-cell' (v1)  / 'free-point' / 'rect' (future)
 *   - allowedLayerIds / allowedLayerKinds gate which layers accept what
 *   - props[] declares typed form fields the inspector renders
 */

import { TrainSimBoard } from '../../games/trainsim/TrainSimBoard.js';
import {
    TRAINSIM_GRID,
    TRAINSIM_CITIES,
    TRAINSIM_CONFIG,
    TILE_DEFS,
    DIR_DELTA,
    OPPOSITE,
    rotateConnections,
    getExitDir,
    deriveSwitchRoutesFromConnections,
    buildTrainsimTrackSvg,
    buildTrainsimRoadSvg,
} from '../../games/trainsim/trainsimConfig.js';

// Pre-extract the city-picker options once so the schema stays
// declarative — both arrays are kept in matching order so a value
// at index N pairs with the human-friendly label at index N.
const CITY_VALUES = TRAINSIM_CITIES.map(c => c.value);
const CITY_LABELS = TRAINSIM_CITIES.map(c => c.label);

export const TRAINSIM_SCHEMA = {
    game: 'trainsim',

    grid: {
        cols: TRAINSIM_GRID.cols,
        rows: TRAINSIM_GRID.rows,
        cellSize: TRAINSIM_GRID.cellSize,
    },

    /**
     * Required gameplay layers — declared here so the runtime knows
     * which layer to read tracks / lamps / switches from. Users can
     * rename them in the editor but not delete them or change their id.
     *
     * `track` carries rails + spawns/goals/lamps/switches (trains).
     * `road`  carries asphalt + car spawns/goals (cars). Both share
     *         the same `lamp` entity for traffic lights — a single
     *         lamp on a road cell stops cars; on a track cell it
     *         stops trains; on a level-crossing cell it does both.
     */
    requiredLayers: [
        { id: 'track', defaultLabel: 'Track', order: 0 },
        { id: 'road',  defaultLabel: 'Road',  order: 1 },
    ],

    /**
     * Default layer set inserted when the user creates a fresh stage.
     * Decoration layers above/below the gameplay layers give them a
     * visible parallax-style stack to start from. All decoration
     * layers are user-deletable. The road layer sits BELOW the track
     * layer so level crossings render with rails on top of asphalt
     * (the visually correct stacking for a railway crossing).
     */
    defaultLayers: [
        { id: 'background', label: 'Background', kind: 'decoration' },
        { id: 'road',       label: 'Road',       kind: 'gameplay'   },
        { id: 'track',      label: 'Track',      kind: 'gameplay'   },
        { id: 'foreground', label: 'Foreground', kind: 'decoration' },
    ],

    tilePalette: [
        // ── Track pieces (gameplay layer only) ────────────────────────────
        // `kind: 'track'` → procedural drawing via drawTrainsimTrack(),
        // routing-aware. Allowed only on the gameplay layer.
        { type: 'straight', label: 'Straight',        group: 'Track', kind: 'track', slot: 'trainsim.track.straight', rotates: true,  allowedLayerIds: ['track'] },
        { type: 'curve',    label: 'Curve (90°)',     group: 'Track', kind: 'track', slot: 'trainsim.track.corner',   rotates: true,  allowedLayerIds: ['track'] },
        { type: 'diagonal', label: 'Diagonal (45°)',  group: 'Track', kind: 'track', slot: null,                      rotates: true,  allowedLayerIds: ['track'] },
        { type: 'bendr',    label: 'Bend 45° ↘',      group: 'Track', kind: 'track', slot: null,                      rotates: true,  allowedLayerIds: ['track'] },
        { type: 'bendl',    label: 'Bend 45° ↙',      group: 'Track', kind: 'track', slot: null,                      rotates: true,  allowedLayerIds: ['track'] },
        { type: 'diagy',    label: 'Diag Y (side)',   group: 'Track', kind: 'track', slot: null,                      rotates: true,  allowedLayerIds: ['track'] },
        { type: 'diagy2',   label: 'Diag Y (end)',    group: 'Track', kind: 'track', slot: null,                      rotates: true,  allowedLayerIds: ['track'] },
        { type: 'wyec',     label: 'Y Branch ↗',      group: 'Track', kind: 'track', slot: null,                      rotates: true,  allowedLayerIds: ['track'] },
        { type: 'wyec2',    label: 'Y Branch ↖',      group: 'Track', kind: 'track', slot: null,                      rotates: true,  allowedLayerIds: ['track'] },
        { type: 't',        label: 'T Junction',      group: 'Track', kind: 'track', slot: 'trainsim.track.t',        rotates: true,  allowedLayerIds: ['track'] },
        { type: 'cross',    label: 'Cross',           group: 'Track', kind: 'track', slot: 'trainsim.track.cross',    rotates: false, allowedLayerIds: ['track'] },
        { type: 'deadend',  label: 'Dead End',        group: 'Track', kind: 'track', slot: 'trainsim.track.deadend',  rotates: true,  allowedLayerIds: ['track'] },

        // ── Road pieces (road layer only) ─────────────────────────────────
        // `kind: 'road'` → procedural drawing via drawTrainsimRoad(),
        // asphalt + dashed centreline. Type strings deliberately mirror
        // the track set ('straight', 'curve', 't', …) since the
        // routing geometry is identical; the layer id disambiguates
        // road tiles from track tiles in the board painter.
        { type: 'straight', label: 'Road Straight',  group: 'Road', kind: 'road', slot: null, rotates: true,  allowedLayerIds: ['road'] },
        { type: 'curve',    label: 'Road Curve',     group: 'Road', kind: 'road', slot: null, rotates: true,  allowedLayerIds: ['road'] },
        { type: 't',        label: 'Road T',         group: 'Road', kind: 'road', slot: null, rotates: true,  allowedLayerIds: ['road'] },
        { type: 'cross',    label: 'Road Cross',     group: 'Road', kind: 'road', slot: null, rotates: false, allowedLayerIds: ['road'] },
        { type: 'deadend',  label: 'Road Dead End',  group: 'Road', kind: 'road', slot: null, rotates: true,  allowedLayerIds: ['road'] },

        // ── Decoration sprites (background / foreground only) ─────────────
        // `kind: 'sprite'` → the board paints the slot's PNG, scaled to
        // `footprint.w × footprint.h` cells. They live on decoration
        // layers, not the gameplay layer, so painting one never affects
        // train routing. `tileType` is the same string used as the
        // SkinResolver slot suffix (e.g. type 'sky.cloud.large.1' ↔
        // slot 'trainsim.sky.cloud.large.1').

        // Sky
        { type: 'sky.cloud.large.1', label: 'Cloud Large 1', group: 'Sky',       kind: 'sprite', slot: 'trainsim.sky.cloud.large.1', footprint: { w: 4, h: 2 }, allowedLayerKinds: ['decoration'] },
        { type: 'sky.cloud.large.2', label: 'Cloud Large 2', group: 'Sky',       kind: 'sprite', slot: 'trainsim.sky.cloud.large.2', footprint: { w: 4, h: 2 }, allowedLayerKinds: ['decoration'] },
        { type: 'sky.cloud.small.1', label: 'Cloud Small 1', group: 'Sky',       kind: 'sprite', slot: 'trainsim.sky.cloud.small.1', footprint: { w: 2, h: 1 }, allowedLayerKinds: ['decoration'] },
        { type: 'sky.cloud.small.2', label: 'Cloud Small 2', group: 'Sky',       kind: 'sprite', slot: 'trainsim.sky.cloud.small.2', footprint: { w: 2, h: 1 }, allowedLayerKinds: ['decoration'] },

        // Nature
        { type: 'obstacle.tree.pine',   label: 'Pine Tree',     group: 'Nature',    kind: 'sprite', slot: 'trainsim.obstacle.tree.pine',   footprint: { w: 1, h: 2 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.tree.single', label: 'Small Tree',    group: 'Nature',    kind: 'sprite', slot: 'trainsim.obstacle.tree.single', footprint: { w: 2, h: 2 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.bush.single', label: 'Green Bush',    group: 'Nature',    kind: 'sprite', slot: 'trainsim.obstacle.bush.single', footprint: { w: 2, h: 2 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.bush.pink',   label: 'Pink Bush',     group: 'Nature',    kind: 'sprite', slot: 'trainsim.obstacle.bush.pink',   footprint: { w: 4, h: 2 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.bush.yellow', label: 'Yellow Bush',   group: 'Nature',    kind: 'sprite', slot: 'trainsim.obstacle.bush.yellow', footprint: { w: 1, h: 1 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.flowerbed',        label: 'Flowerbed',        group: 'Nature', kind: 'sprite', slot: 'trainsim.obstacle.flowerbed',        footprint: { w: 2, h: 1 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.flowerbed.orange', label: 'Orange Flowerbed', group: 'Nature', kind: 'sprite', slot: 'trainsim.obstacle.flowerbed.orange', footprint: { w: 1, h: 1 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.rock.single', label: 'Small Stone',   group: 'Nature',    kind: 'sprite', slot: 'trainsim.obstacle.rock.single', footprint: { w: 2, h: 2 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.rock.large',  label: 'Boulder',       group: 'Nature',    kind: 'sprite', slot: 'trainsim.obstacle.rock.large',  footprint: { w: 3, h: 2 }, allowedLayerKinds: ['decoration'] },

        // Street furniture
        { type: 'obstacle.lamp',           label: 'Street Lamp',    group: 'Street',    kind: 'sprite', slot: 'trainsim.obstacle.lamp',           footprint: { w: 1, h: 2 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.traffic_lamp',   label: 'Traffic Light',  group: 'Street',    kind: 'sprite', slot: 'trainsim.obstacle.traffic_lamp',   footprint: { w: 1, h: 2 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.bench',          label: 'Park Bench',     group: 'Street',    kind: 'sprite', slot: 'trainsim.obstacle.bench',          footprint: { w: 2, h: 1 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.barrier',        label: 'Barrier',        group: 'Street',    kind: 'sprite', slot: 'trainsim.obstacle.barrier',        footprint: { w: 3, h: 2 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.stop',           label: 'Stop Sign',      group: 'Street',    kind: 'sprite', slot: 'trainsim.obstacle.stop',           footprint: { w: 2, h: 2 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.icecream_truck', label: 'Ice-cream Truck',group: 'Street',    kind: 'sprite', slot: 'trainsim.obstacle.icecream_truck', footprint: { w: 2, h: 3 }, allowedLayerKinds: ['decoration'] },

        // Buildings
        { type: 'obstacle.house',               label: 'House',         group: 'Buildings', kind: 'sprite', slot: 'trainsim.obstacle.house',               footprint: { w: 5, h: 3 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.building.large',      label: 'Building',      group: 'Buildings', kind: 'sprite', slot: 'trainsim.obstacle.building.large',      footprint: { w: 3, h: 3 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.building.modern',     label: 'Modern Building',group: 'Buildings', kind: 'sprite', slot: 'trainsim.obstacle.building.modern',    footprint: { w: 3, h: 2 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.building.pink',       label: 'Pink Building', group: 'Buildings', kind: 'sprite', slot: 'trainsim.obstacle.building.pink',       footprint: { w: 4, h: 4 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.building.hospital',   label: 'Hospital',      group: 'Buildings', kind: 'sprite', slot: 'trainsim.obstacle.building.hospital',   footprint: { w: 4, h: 4 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.building.restaurant', label: 'Restaurant',    group: 'Buildings', kind: 'sprite', slot: 'trainsim.obstacle.building.restaurant', footprint: { w: 3, h: 2 }, allowedLayerKinds: ['decoration'] },
        { type: 'obstacle.building.vegies',     label: 'Veggie Stand',  group: 'Buildings', kind: 'sprite', slot: 'trainsim.obstacle.building.vegies',     footprint: { w: 2, h: 2 }, allowedLayerKinds: ['decoration'] },

        // Stations (as decorations — the gameplay `station` entity is
        // separate, this one is purely cosmetic and gets baked into the
        // background/foreground).
        { type: 'station.single',  label: 'Tram Station (Single)', group: 'Stations', kind: 'sprite', slot: 'trainsim.station',        footprint: { w: 4, h: 2 }, allowedLayerKinds: ['decoration'] },
        { type: 'station.double',  label: 'Tram Station (Double)', group: 'Stations', kind: 'sprite', slot: 'trainsim.station.double', footprint: { w: 4, h: 2 }, allowedLayerKinds: ['decoration'] },
    ],

    /**
     * Renderer adapter — the editor reads `boardClass` + `boardOptions`
     * to instantiate the same board component the live game uses.
     * `paletteSvgFor` builds inline SVG previews for tile palette
     * thumbnails (track + road tiles get geometry-accurate icons;
     * sprite tiles return null so the palette falls back to PNG
     * thumbnails resolved through SkinResolver).
     * `playScene` names the SceneManager scene the "Test Play"
     * button hands the working draft off to.
     */
    renderer: {
        boardClass: TrainSimBoard,
        boardOptions: { editorMode: true },
        playScene: 'trainsim',
        paletteSvgFor(tdef, rot, opts = {}) {
            if (tdef.kind === 'road')  return buildTrainsimRoadSvg(tdef.type, rot, opts);
            if (tdef.kind === 'track') return buildTrainsimTrackSvg(tdef.type, rot, opts);
            return null;
        },
    },

    /**
     * Track-routing helpers — exposed so the editor's structural
     * validator (`_runFullValidation`) can compute connection masks,
     * BFS reachability, switch-routing tables, and crash simulation
     * without importing trainsim modules directly. Schemas without
     * track tiles (e.g. the platformer) simply omit this block, and
     * the validator's track-aware passes self-skip.
     */
    trackHelpers: {
        TILE_DEFS,
        DIR_DELTA,
        OPPOSITE,
        rotateConnections,
        getExitDir,
        deriveSwitchRoutesFromConnections,
        baseSpeed: TRAINSIM_CONFIG.TRAIN_BASE_SPEED,
    },

    entityTypes: [
        {
            type: 'spawn',
            label: 'Train Spawn',
            positioning: 'tile-cell',
            allowedLayerIds: ['track'],
            max: 5,
            // `slotPerInstance` is resolved by the editor + game so the
            // sprite differs per train id (colour-coded station marker
            // PNGs — Keleti, Nyugati, Szombathely, Szolnok, Miskolc).
            slotPerInstance: (e) => `trainsim.spawn.${e?.props?.trainId || 't1'}`,
            props: [
                { key: 'trainId',    kind: 'choice', options: ['t1', 't2', 't3', 't4', 't5'], label: 'Train ID' },
                { key: 'color',      kind: 'choice', options: ['red', 'blue', 'yellow', 'green', 'purple'], label: 'Color' },
                { key: 'dir',        kind: 'choice',
                  options:      [0,       1,            2,      3,            4,       5,            6,      7],
                  optionLabels: ['North', 'North-East', 'East', 'South-East', 'South', 'South-West', 'West', 'North-West'],
                  label: 'Initial Direction' },
                { key: 'startDelay', kind: 'number', min: 0, max: 30, step: 0.5, label: 'Start Delay (s)' },
            ],
        },
        {
            type: 'goal',
            label: 'Train Goal',
            positioning: 'tile-cell',
            allowedLayerIds: ['track'],
            max: 5,
            slotPerInstance: (e) => `trainsim.goal.${e?.props?.trainId || 't1'}`,
            props: [
                { key: 'trainId', kind: 'choice', options: ['t1', 't2', 't3', 't4', 't5'], label: 'Train ID' },
                // City name shown UNDER the stop sign so the player
                // knows "this is where train T1 should end up". Pick
                // any city from the catalog; '' falls back to the
                // train's origin city (legacy behaviour).
                { key: 'destinationCity', kind: 'choice',
                  options: CITY_VALUES, optionLabels: CITY_LABELS,
                  label: 'Destination City' },
            ],
        },
        {
            type: 'lamp',
            label: 'Barrier (Sorompó)',
            positioning: 'tile-cell',
            // The 'lamp' entity is now a car-only, fully AUTOMATIC
            // level-crossing barrier (sorompó). Place it on a level-
            // crossing cell (track + road overlap) or next to a road;
            // it drops automatically when a train nears and raises
            // after it passes. It never stops a train and has no
            // player-toggled state — trains are stopped by per-switch
            // signals instead.
            allowedLayerIds: ['track', 'road'],
            slot: 'trainsim.tile.lamp',
            props: [],
        },
        {
            type: 'switch',
            label: 'Track Switch (+ stop/go signal)',
            positioning: 'tile-cell',
            allowedLayerIds: ['track'],
            slot: 'trainsim.tile.switch',
            props: [
                { key: 'baseType', kind: 'choice', options: ['t', 'cross'], label: 'Junction Shape' },
                { key: 'rot',      kind: 'number', min: 0, max: 3, step: 1, label: 'Rotation (0-3)' },
                // `initial` is now a 0-based routing index — a T or
                // cross derives 3 states, a 2-port "junction" only 1.
                // Out-of-range values wrap mod the live state count
                // at load time, so 0..5 is always safe.
                { key: 'initial',  kind: 'number', min: 0, max: 5, step: 1, label: 'Initial State (0-based)' },
                // Per-switch stop/go SIGNAL (the train stop control).
                // 'auto' randomizes red/green at stage start in the
                // live game; 'red'/'green' pin the editor preview.
                { key: 'signal',   kind: 'choice',
                  options: ['auto', 'red', 'green'],
                  optionLabels: ['Random', 'Red (stop)', 'Green (go)'],
                  label: 'Signal (stop/go)' },
            ],
        },
        {
            type: 'station',
            label: 'Station',
            positioning: 'tile-cell',
            allowedLayerIds: ['track'],
            slot: 'trainsim.station',
            props: [],
        },
    ],
};

/**
 * Tiny registry so future games can plug their schema in without
 * touching the editor scene. Each entry: gameId → schema descriptor
 * the StageEditorScene reads in init() based on `editorTargetGame`.
 */
import { PLATFORMER_SCHEMA } from './platformer.schema.js';

export const SCHEMA_REGISTRY = {
    trainsim:   TRAINSIM_SCHEMA,
    platformer: PLATFORMER_SCHEMA,
};
